import clsx from "clsx";
import date from "date-and-time";

import {
  CoreDeploymentRunStatus,
  CoreDeploymentTargetStatus,
  CoreTargetBranch,
} from "../../models/CoreDeployment/CoreDeploymentRun";

export const runStatusLabels: Record<CoreDeploymentRunStatus, string> = {
  Scheduled: "Scheduled",
  WaitingForStaging: "Checking staging",
  PreparingPullRequests: "Opening PRs",
  Merging: "Merging",
  Deploying: "Deploying",
  Succeeded: "Succeeded",
  Failed: "Failed",
  Cancelled: "Cancelled",
};

export const runStatusClassMap: Record<CoreDeploymentRunStatus, string> = {
  Scheduled: "bg-blue-100 text-blue-700",
  WaitingForStaging: "bg-yellow-100 text-yellow-700",
  PreparingPullRequests: "bg-yellow-100 text-yellow-700",
  Merging: "bg-yellow-100 text-yellow-700",
  Deploying: "bg-yellow-100 text-yellow-700",
  Succeeded: "bg-green-100 text-green-700",
  Failed: "bg-red-100 text-red-700",
  Cancelled: "bg-gray-200 text-gray-700",
};

export const targetStatusLabels: Record<CoreDeploymentTargetStatus, string> = {
  Pending: "Pending",
  NothingToDeploy: "Nothing to deploy",
  Merged: "Merged, deploying",
  MergeFailed: "Merge failed",
  DeploySucceeded: "Deployed",
  DeployFailed: "Deploy failed",
  Skipped: "Skipped",
};

export const targetStatusClassMap: Record<CoreDeploymentTargetStatus, string> = {
  Pending: "bg-gray-100 text-gray-700",
  NothingToDeploy: "bg-blue-100 text-blue-700",
  Merged: "bg-yellow-100 text-yellow-700",
  MergeFailed: "bg-red-100 text-red-700",
  DeploySucceeded: "bg-green-100 text-green-700",
  DeployFailed: "bg-red-100 text-red-700",
  Skipped: "bg-gray-200 text-gray-600",
};

export const targetBranchLabels: Record<CoreTargetBranch, string> = {
  "core-deployment": "Core",
  "tme-deployment": "TME",
};

export const targetBranchClassMap: Record<CoreTargetBranch, string> = {
  "core-deployment": "bg-indigo-100 text-indigo-700",
  "tme-deployment": "bg-teal-100 text-teal-700",
};

const pillBaseClasses =
  "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap";

interface PillProps {
  className?: string;
}

export function CoreDeploymentStatusPill({
  status,
  className,
}: PillProps & { status: CoreDeploymentRunStatus }) {
  return (
    <span className={clsx(pillBaseClasses, runStatusClassMap[status] ?? "bg-gray-100 text-gray-700", className)}>
      {runStatusLabels[status] ?? status}
    </span>
  );
}

export function CoreDeploymentTargetStatusPill({
  status,
  className,
}: PillProps & { status: CoreDeploymentTargetStatus }) {
  return (
    <span className={clsx(pillBaseClasses, targetStatusClassMap[status] ?? "bg-gray-100 text-gray-700", className)}>
      {targetStatusLabels[status] ?? status}
    </span>
  );
}

export function CoreTargetBranchPill({
  branch,
  className,
}: PillProps & { branch: CoreTargetBranch }) {
  return (
    <span
      className={clsx(pillBaseClasses, targetBranchClassMap[branch] ?? "bg-gray-100 text-gray-700", className)}
      title={branch}
    >
      {targetBranchLabels[branch] ?? branch}
    </span>
  );
}

/** Workflow conclusions as GitHub reports them, coloured like the target pills. */
export function workflowConclusionClass(status: string, conclusion: string | null): string {
  if (status !== "completed") return "text-yellow-700";
  switch (conclusion) {
    case "success":
    case "skipped":
    case "neutral":
      return "text-green-700";
    case null:
      return "text-gray-600";
    default:
      return "text-red-700";
  }
}

export function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? "-" : date.format(parsed, "DD/MM/YYYY HH:mm");
}

export function shortSha(sha?: string | null): string {
  return sha ? sha.substring(0, 7) : "-";
}

/** Local wall-clock value for a datetime-local input, e.g. "2026-10-30T22:30". */
export function toDateTimeLocalValue(value: Date): string {
  return date.format(value, "YYYY-MM-DDTHH:mm");
}
